import { SelectField } from '@almbrand/selectfield';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import classNames from 'classnames';
import { useSpdSearch } from 'hooks';
import { useEffect, useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Obj } from 'src/hooks/useSpdSearch';
import styles from './CarSearch.module.scss';
import { isSearchResultValid } from './validateCarSearch';

type OptionsState = {
	licensePlate: { errorMessage?: string; options: Option[] };
	brand: { errorMessage?: string; options: Option[] };
	year: { errorMessage?: string; options: Option[] };
	model: { errorMessage?: string; options: Option[] };
	variant: { errorMessage?: string; options: Option[] };
};

export type Option = {
	value: string;
	label: string;
};

export interface CarMakeAndModelProps {
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
	themeName?: ThemeUnion;
	saveOutputFieldsToForm: (
		register: UseFormRegister<FieldValues>,
		searchConfiguration: SpdSearchType['searchConfiguration'],
		value: Option | Obj,
		contentType: string,
		completeCarSearch?: boolean
	) => void;
	register: UseFormRegister<FieldValues>;
	contentType: string;
}

export const CarMakeAndModel: React.FC<CarMakeAndModelProps> = ({
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	themeName,
	saveOutputFieldsToForm,
	register,
	contentType,
}) => {
	const makeSearch = useSpdSearch({ spdSearchType: makeSearchField });
	const yearSearch = useSpdSearch({ spdSearchType: yearSearchField });
	const modelSearch = useSpdSearch({ spdSearchType: modelSearchField });
	const variantSearch = useSpdSearch({ spdSearchType: variantSearchField });

	const [makeAndModelState, setMakeAndModelState] = useState<
		{ name: string; param: { param_name: string; param_value: string } }[]
	>([]);
	const [options, setOptions] = useState<OptionsState>({
		licensePlate: { options: [] },
		brand: { options: [] },
		year: { options: [] },
		model: { options: [] },
		variant: { options: [] },
	});

	const [selectFieldKeys, setSelectFieldKeys] = useState({
		model: 0,
		variant: 0,
		year: 0,
	});

	const [loading, setLoading] = useState({
		brand: false,
		year: false,
		model: false,
		variant: false,
	});

	useEffect(() => {
		populateBrandOptions('1');
	}, []);

	useEffect(() => {
		// Only run if the last updated field was the brand (makeSearchField)
		const last = makeAndModelState.at(-1);
		if (last?.name === makeSearchField.uniqueIdentifier.uid) {
			populateModelOptions();
		}

		if (last?.name === modelSearchField.uniqueIdentifier.uid) {
			populateYearOptions();
		}

		if (last?.name === yearSearchField.uniqueIdentifier.uid) {
			populateVariantOptions();
		}

		if (last?.name === variantSearchField.uniqueIdentifier.uid) {
			saveVariantDetailsToForm();
		}
	}, [makeAndModelState]);

	const populateBrandOptions = async (staticValue: string) => {
		setLoading((prev) => ({ ...prev, brand: true }));
		const params = buildParams(makeSearchField.searchConfiguration, makeAndModelState, '1');

		const result = await makeSearch.doSearch({
			name: makeSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});

		if (Array.isArray(result)) {
			setLoading((prev) => ({ ...prev, brand: false }));

			params.forEach((param) =>
				setMakeAndModelState((state) => [
					...state,
					{
						name:
							makeSearchField.searchConfiguration.outputFields.items.find(
								(field) => field.content.param === param.param_name
							)?.content.uniqueIdentifier.uid ?? '',
						param: { param_name: param.param_name, param_value: staticValue },
					},
				])
			);

			setOptionsFromSearchResult('brand', result);
		}
	};

	const handleBrandChange = (value: Option): void => {
		clearOptionGroups(['model', 'variant', 'year']);
		updateMakeAndModelState(setMakeAndModelState, makeSearchField, value);
		saveOutputFieldsToForm(register, makeSearchField.searchConfiguration, value, contentType);
	};

	const populateModelOptions = async () => {
		setLoading((prev) => ({ ...prev, model: true }));
		let params = buildParams(modelSearchField.searchConfiguration, makeAndModelState);
		const result = await modelSearch.doSearch({
			name: modelSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoading((prev) => ({ ...prev, model: false }));
			setOptionsFromSearchResult('model', result);
		}
	};

	const handleModelChange = (value: Option): void => {
		clearOptionGroups(['variant', 'year']);
		updateMakeAndModelState(setMakeAndModelState, modelSearchField, value);
		saveOutputFieldsToForm(register, modelSearchField.searchConfiguration, value, contentType);
	};

	const populateYearOptions = async () => {
		setLoading((prev) => ({ ...prev, year: true }));
		const params = buildParams(yearSearchField.searchConfiguration, makeAndModelState);
		const result = await yearSearch.doSearch({
			name: yearSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoading((prev) => ({ ...prev, year: false }));
			setOptionsFromSearchResult('year', result);
		}
	};

	const handleYearChange = (value: Option): void => {
		clearOptionGroups(['variant']);
		updateMakeAndModelState(setMakeAndModelState, yearSearchField, value);
		saveOutputFieldsToForm(register, yearSearchField.searchConfiguration, value, contentType);
	};

	const populateVariantOptions = async () => {
		setLoading((prev) => ({ ...prev, variant: true }));
		let params = buildParams(variantSearchField.searchConfiguration, makeAndModelState);
		const result = await variantSearch.doSearch({
			name: variantSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoading((prev) => ({ ...prev, variant: false }));
			setOptionsFromSearchResult('variant', result);
		}
	};

	const handleVariantChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, variantSearchField, value);
		saveOutputFieldsToForm(register, variantSearchField.searchConfiguration, value, contentType);
	};

	const saveVariantDetailsToForm = async () => {
		const params = buildParams(variantSearchField.secondarySearchConfiguration, makeAndModelState);
		const result = await variantSearch.doSearch({
			name: 'VEHICLE_VARIANT_DETAILS_CODAN',
			params,
		});
		if (isSearchResultValid(variantSearchField.secondarySearchConfiguration, result as Obj)) {
			saveOutputFieldsToForm(
				register,
				variantSearchField.secondarySearchConfiguration,
				result as Obj,
				contentType,
				true
			);
		}
	};

	const buildParams = (
		searchConfiguration: SpdSearchType['searchConfiguration'],
		makeAndModelState: { name: string; param: { param_name: string; param_value: string } }[],
		optionalValue?: string
	) => {
		return searchConfiguration.inputFields.items.map((item) => {
			const found = makeAndModelState.find((state) => state.name === item.content.uniqueIdentifier.uid);

			return found ? found.param : { param_name: item.content.param, param_value: optionalValue ?? '' };
		});
	};

	const setOptionsFromSearchResult = (
		key: keyof Pick<OptionsState, 'brand' | 'year' | 'model' | 'variant'>,
		result: Obj | Option[]
	) => {
		setOptions((prev) => ({
			...prev,
			[key]: {
				...prev[key],
				options: Array.isArray(result) ? result : [],
			},
		}));
	};

	const updateMakeAndModelState = (
		setState: React.Dispatch<
			React.SetStateAction<{ name: string; param: { param_name: string; param_value: string } }[]>
		>,
		field: SpdSearchType,
		value: Option
	) => {
		setState((state) => {
			const existingIndex = state.findIndex((item) => item.name === field.uniqueIdentifier.uid);
			// const paramName = field.searchConfiguration.outputFields.items[0].content.param;

			const outputField = field.searchConfiguration.outputFields.items;

			if (existingIndex !== -1) {
				// Update the existing item and remove all items after it
				const updated = state
					.slice(0, existingIndex + 1)
					.map((item, index) =>
						index === existingIndex ? { ...item, param: { ...item.param, param_value: value.value } } : item
					);

				return updated;
			}

			return [
				...state,
				...outputField
					.filter((output) => !state.some((item) => item.name === output.content.uniqueIdentifier.uid))
					.map((output) => ({
						name: output.content.uniqueIdentifier.uid,
						param: {
							param_name: output.content.param,
							param_value: value.value,
						},
					})),
			];
		});
	};

	const clearOptionGroups = (keys: Array<'model' | 'variant' | 'year'>) => {
		setOptions((prev) => {
			const next = { ...prev };
			keys.forEach((key) => {
				next[key] = { ...prev[key], options: [] };
			});
			return next;
		});

		setSelectFieldKeys((prev) => {
			const next = { ...prev };
			keys.forEach((key) => {
				next[key] = prev[key] + 1;
			});
			return next;
		});
	};

	return (
		<>
			<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldBrand)}>
				<SelectField
					themeName={themeName}
					options={options.brand.options}
					errorMessage={options.brand.errorMessage}
					icon={{ isIcon: false }}
					placeHolder='Bilmærke'
					isSearchable
					iconPosition='right'
					onChange={handleBrandChange}
					isLoading={loading.brand}
				/>
			</div>
			<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldModel)}>
				<SelectField
					themeName={themeName}
					key={`model-${selectFieldKeys.model}`}
					options={options.model.options}
					isDisabled={!options.model.options.length}
					errorMessage={options.model.errorMessage}
					icon={{ isIcon: false }}
					placeHolder='Model'
					isSearchable
					iconPosition='right'
					onChange={handleModelChange}
					isLoading={loading.model}
				/>
			</div>
			<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldYear)}>
				<SelectField
					options={options.year.options}
					isDisabled={!options.brand.options.length}
					errorMessage={options.year.errorMessage}
					icon={{ isIcon: false }}
					placeHolder='Årgang'
					isSearchable
					iconPosition='right'
					onChange={handleYearChange}
					isLoading={loading.year}
				/>
			</div>
			<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldVariant)}>
				<SelectField
					themeName={themeName}
					key={`variant-${selectFieldKeys.variant}`}
					options={options.variant.options}
					isDisabled={!options.model.options.length}
					errorMessage={options.variant.errorMessage}
					icon={{ isIcon: false }}
					placeHolder='Variant'
					isSearchable
					iconPosition='right'
					onChange={handleVariantChange}
					isLoading={loading.variant}
				/>
			</div>
		</>
	);
};
